import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, PageHeader, Alert } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

const PLAN_NAMES = { pack_one:'Pack One', pack_two:'Pack Two', pack_advanced:'Pack Advanced' };

export default function BankTransfers() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [acting, setActing]       = useState('');

  useEffect(() => { fetchTransfers(); }, []);

  const fetchTransfers = async () => {
    setLoading(true);
    try { const r = await api.get('/superadmin/bank-transfers'); setTransfers(r.data.transfers || []); }
    catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  const approve = async (payment) => {
    if (!window.confirm(`Approve payment from client?\nRef: ${payment.gatewayOrderId}\nPlan: ${PLAN_NAMES[payment.planId]}\nAmount: €${payment.amount}`)) return;
    setActing(payment.id);
    try { await api.post(`/superadmin/bank-transfers/${payment.id}/approve`); toast.success('✅ Payment approved! Account activated.'); fetchTransfers(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setActing(''); }
  };

  const reject = async (payment) => {
    if (!window.confirm(`Reject this bank transfer?\nRef: ${payment.gatewayOrderId}`)) return;
    setActing(payment.id);
    try { await api.post(`/superadmin/bank-transfers/${payment.id}/reject`); toast.success('Transfer rejected'); fetchTransfers(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setActing(''); }
  };

  return (
    <div>
      <PageHeader title="Bank Transfer Requests" subtitle={`${transfers.length} pending verification`} action={
        <Button variant="secondary" onClick={fetchTransfers} size="sm">🔄 Refresh</Button>
      } />

      {transfers.length > 0 && (
        <Alert type="warning" style={{ marginBottom: 20 }}>
          {transfers.length} client{transfers.length > 1 ? 's have' : ' has'} submitted bank transfer references and {transfers.length > 1 ? 'are' : 'is'} waiting for account activation.
        </Alert>
      )}

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : (
        transfers.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🏦</div>
            <h3 style={{ color: '#ccc', marginBottom: 8 }}>No pending bank transfers</h3>
            <p style={{ color: '#555', fontSize: 14 }}>Bank transfer requests from clients will appear here for your approval.</p>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {transfers.map(t => (
              <Card key={t.id} style={{ border: '1px solid rgba(243,156,18,0.25)', background: 'rgba(243,156,18,0.02)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <Badge color="warning">⏳ Pending Verification</Badge>
                      <span style={{ fontSize: 12, color: '#555' }}>{t.createdAt ? new Date(t.createdAt).toLocaleString() : ''}</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                      {[
                        ['Client ID', t.userId?.slice(0,12) + '...'],
                        ['Plan', PLAN_NAMES[t.planId] || t.planId],
                        ['Amount', `€${t.amount}`],
                        ['Currency', t.currency || 'EUR'],
                      ].map(([l, v]) => (
                        <div key={l}>
                          <div style={{ fontSize: 11, color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>{l}</div>
                          <div style={{ fontSize: 14, color: '#ccc', fontWeight: 600 }}>{v}</div>
                        </div>
                      ))}
                    </div>

                    {/* Reference number — prominent */}
                    <div style={{ marginTop: 14, background: '#1a1a1a', borderRadius: 10, padding: '12px 16px', border: '1px solid rgba(46,204,113,0.2)' }}>
                      <div style={{ fontSize: 11, color: '#2ECC71', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Bank Transaction / Reference Number</div>
                      <div style={{ fontSize: 17, fontWeight: 900, color: '#fff', fontFamily: 'monospace', letterSpacing: 0.5 }}>{t.gatewayOrderId}</div>
                    </div>

                    {t.notes && (
                      <div style={{ marginTop: 10, background: '#161616', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#888', lineHeight: 1.5 }}>
                        <span style={{ color: '#555', fontWeight: 700 }}>Notes: </span>{t.notes}
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 160 }}>
                    <Button
                      loading={acting === t.id}
                      onClick={() => approve(t)}
                      style={{ background: '#2ECC71', color: '#000', fontWeight: 900 }}>
                      ✅ Approve & Activate
                    </Button>
                    <Button
                      variant="danger"
                      loading={acting === t.id}
                      onClick={() => reject(t)}>
                      ❌ Reject
                    </Button>
                    <div style={{ fontSize: 11, color: '#444', textAlign: 'center', lineHeight: 1.5 }}>
                      Verify the reference in your bank portal before approving
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}
